* {
    box-sizing: border-box;
}

/**
 * @prop --primary-color: Sets the primary theming color 
 * @prop --secondary-color: Sets the secondary theming color 
 * @prop --tertiary-color: Sets the tertiary theming color 
 * @prop --focus-color: Sets the color of the outline on focus.
 * @prop --foreground-color: Directly set the foreground color of the button. 
 * @prop --background-color: Directly set the background color of the button. 
 * @prop --border-color:  Directly set the border color of the button. 
 * @prop --border-radius: The border radius of the button.
 * @prop --border-width: The border width of the button.
 * @prop --text-decoration: The text decoration of the button.
 * @prop --transition-duration: The transition duration of the button.
 * @prop --spacing: Internal spacing of the button (padding and between slots).
 * @prop --align-inner: The flex align of the button content.
 */

:host {
    --height: 40px;

    --border-style: solid;
    --border-radius: 30px;
    --border-width: 2px;
    --text-decoration: none;
    --transition-duration: 500ms;
    --spacing: 14px;
    --align-inner: center;
    --focus: var(--focus-color, var(--color-focus));

    font-size: 16px;
    display: inline-block;
    height: 40px;
    height: var(--height);
    border-radius: 30px;
    border-radius: var(--border-radius);
}

:host(:not([disabled]):focus),
:host(:not([disabled]):focus-within) {
    outline-offset: 2px;
    outline-color: var(--focus);
    outline-style: solid;
}

/* variants */
:host([variant='default']) {
    ---primary: var(--primary-color, var(--color-background));
    ---secondary: var(--secondary-color, var(--color-secondary));
    ---tertiary: var(--tertiary-color, var(--color-shade-30));

    --background-color: var(---primary);
    --foreground-color: var(---secondary);
    --border-color: var(---tertiary);
}
:host([variant='default']:not([disabled]):hover) {
    --background-color: var(---secondary);
    --foreground-color: var(---primary);
    --border-color: var(---primary);
}
:host([variant='default'][disabled]) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-background);
    --border-color: var(--color-shade-40);
}

:host([variant='filled']) {
    ---primary: var(--primary-color, var(--color-secondary));
    ---secondary: var(--secondary-color, var(--color-background));
    ---tertiary: var(--tertiary-color, var(--color-secondary));

    --background-color: var(---primary);
    --foreground-color: var(---secondary);
    --border-color: var(---tertiary);
}
:host([variant='filled']:not([disabled]):hover) {
    --background-color: var(---secondary);
    --foreground-color: var(---primary);
    --border-color: var(---tertiary);
}
:host([variant='filled'][disabled]) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-background);
    --border-color: var(--color-shade-40);
}

:host([variant='outline']) {
    ---primary: var(--primary-color, var(--color-background));
    ---secondary: var(--secondary-color, var(--color-secondary));
    ---tertiary: var(--tertiary-color, var(--color-secondary));

    --background-color: var(---primary);
    --foreground-color: var(---secondary);
    --border-color: var(---tertiary);
}
:host([variant='outline']:not([disabled]):hover) {
    --background-color: var(---secondary);
    --foreground-color: var(---primary);
    --border-color: var(---tertiary);
}
:host([variant='outline'][disabled]) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-background);
    --border-color: var(--color-shade-40);
}

:host([variant='delete']) {
    --background-color: var(--color-background);
    --foreground-color: var(--color-error);
    --border-color: var(--color-error);
}
:host([variant='delete'][dark]) {
    ---secondary: var(--color-text);
}
:host([variant='delete']:not([disabled]):hover) {
    --background-color: var(--color-error);
    --foreground-color: var(--color-error-contrast);
    --border-color: var(--color-error);
}
:host([variant='delete'][disabled]) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-background);
    --border-color: var(--color-shade-40);
}

:host([variant='cancel']) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-text);
    --border-color: var(--color-shade-40);
}
:host([variant='cancel'][high-contrast]) {
    --background-color: var(--color-background);
}
:host([variant='cancel']:not([disabled]):hover) {
    --background-color: var(--color-background);
    --foreground-color: var(--color-text);
    --border-color: var(--color-shade-40);
}
:host([variant='cancel'][high-contrast]:not([disabled]):hover) {
    --background-color: var(--color-text);
    --foreground-color: var(--color-background);
}
:host([variant='cancel']:not([disabled]):focus),
:host([variant='cancel']:not([disabled]):focus-within) {
    --border-color: var(--color-focus);
}
:host([variant='cancel'][disabled]) {
    --background-color: var(--color-shade-40);
    --foreground-color: var(--color-background);
    --border-color: var(--color-shade-40);
}

:host([variant='minimal']) {
    ---primary: var(--primary-color, var(--color-text));
    ---secondary: var(--secondary-color, var(--color-secondary));

    --background-color: transparent;
    --foreground-color: var(---primary);
    --border-color: transparent;
    --border-width: 0px;
    --border-radius: 0px;
}
:host([variant='minimal']:not([disabled]):hover) {
    --foreground-color: var(---secondary);
}
:host([variant='minimal']:not([disabled]):focus),
:host([variant='minimal']:not([disabled]):focus-within) {
    --foreground-color: var(--color-focus);
}
:host([variant='minimal'][disabled]) {
    --foreground-color: var(--color-shade-40);
}

:host([variant='link']) {
    --background-color: transparent;
    --foreground-color: var(--color-secondary);
    --border-color: transparent;
    --border-width: 0px;
    --border-radius: 0px;
    --height: auto;
    font-size: inherit;
}
:host([variant='link']) button,
:host([variant='link']) a {
    padding: 0;
}
:host([variant='link']:not([disabled]):hover) {
    --text-decoration: underline;
}
:host([variant='link']:not([disabled]):focus),
:host([variant='link']:not([disabled]):focus-within) {
    --text-decoration: underline;
    --foreground-color: var(--color-focus);
}
:host([variant='link'][disabled]) {
    --foreground-color: var(--color-shade-40);
}

:host([variant][high-contrast][disabled]) {
    --background-color: var(--color-background);
    --foreground-color: var(--color-text);
    --border-style: dashed;
}

button,
a {
    display: flex;
    height: 100%;
    width: 100%;
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    font-weight: 400;
    outline: none;
    border-style: solid;
    align-items: center;
    justify-content: space-between;
    font: inherit;
    overflow: hidden;

    padding-top: 0;
    padding-bottom: 0;
    padding-left: var(--spacing);
    padding-right: var(--spacing);
    border-radius: var(--border-radius);
    border-width: var(--border-width);
    border-style: var(--border-style);
    -webkit-text-decoration: var(--text-decoration);
            text-decoration: var(--text-decoration);

    color: var(--foreground-color);
    background-color: var(--background-color);
    border-color: var(--border-color);
}

button:disabled,
    a:disabled,
    button[disabled],
    a[disabled] {
        cursor: not-allowed;
    }

:host(.hydrated) button,
:host(.hydrated) a {
    transition-property: color, border-color, background-color;
    transition-duration: var(--transition-duration);
    transition-timing-function: ease;
}

::slotted([slot='before']) {
    margin-left: 0;
    margin-right: var(--spacing);
}

::slotted([slot='after']) {
    margin-left: var(--spacing);
    margin-right: 0;
}

span {
    display: flex;
    justify-content: var(--align-inner);
    flex: 1 1 100%;
}

::slotted(:where(es-icon, es-badge):not([slot]):only-child) {
    margin-left: calc(0px - var(--spacing));
    margin-right: calc(0px - var(--spacing));
    width: calc(var(--height) - var(--border-width));
}
